import { DrawerDialogComponent } from '@theme/drawer-dialog';

/**
 * A custom element that manages the main menu drawer.
 *
 * Owns root drawer open/close behavior and stagger-animation hooks. The legacy
 * JS-driven nested subpanel/back-button flow, Escape-closes-submenu handling,
 * accordion auto-open and sessionStorage accordion persistence remain absent.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The root dialog element.
 *
 * @extends {DrawerDialogComponent<Refs>}
 */
class HeaderDrawer extends DrawerDialogComponent {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();
    this.#setupAnimatedElementListeners();
  }

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Close the root drawer. Bound from the close button via `on:click="/closeRoot"`.
   */
  closeRoot() {
    return this.close();
  }

  afterDrawerClose() {
    this.refs.trigger?.focus({ preventScroll: true });
  }

  /**
   * Release `will-change` once the entrance animation finishes.
   *
   * `drawer-motion.css` pins `will-change: transform, filter, opacity` on
   * `.drawer-stagger-item`, and only unsets it for the cart drawer's
   * `data-stagger-mode="none"` case. Without this cleanup the menu keeps a
   * compositor layer per item for the life of the page.
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }

    this.querySelectorAll('.drawer-stagger-item').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
